import { useRef, useEffect } from "react";
import { initVoiceRecording, finalizeVoiceRecording } from "../../api.js";
import { getHeadersWithUniqueVisitorId } from "../../../Auth/index.js";
const AUDIO_BITRATE = 32e3;
function pickCodec() {
  if (typeof window === "undefined") return null;
  const M = window.MediaRecorder;
  if (!M || typeof M.isTypeSupported !== "function") return null;
  const candidates = [
    { mime: "audio/webm;codecs=opus", ext: "webm" },
    { mime: "audio/webm", ext: "webm" },
    { mime: "audio/mp4;codecs=mp4a.40.2", ext: "mp4" },
    { mime: "audio/mp4", ext: "mp4" },
    { mime: "audio/ogg;codecs=opus", ext: "ogg" }
  ];
  for (const c of candidates) {
    try {
      if (M.isTypeSupported(c.mime)) return c;
    } catch {
    }
  }
  return null;
}
function useVoiceRecorder(params) {
  const { conversationId, userStream, assistantStream, enabled } = params;
  const initStateRef = useRef(null);
  const userTrackRef = useRef(null);
  const assistantTrackRef = useRef(null);
  useEffect(() => {
    if (!enabled || !conversationId || !userStream) return;
    if (initStateRef.current) return;
    const codec = pickCodec();
    initStateRef.current = {
      initialized: false,
      enabled: false,
      cancelled: false,
      codec
    };
    if (!codec) {
      console.warn("[voice-recorder] no MediaRecorder codec supported; recording disabled");
      return;
    }
    const initState = initStateRef.current;
    initState.inFlightInit = (async () => {
      var _a, _b;
      const headersToInject = await getHeadersWithUniqueVisitorId().catch(() => ({}));
      const result = await initVoiceRecording({
        conversationId,
        mimeUser: codec.mime,
        mimeAssistant: codec.mime,
        extUser: codec.ext,
        extAssistant: codec.ext,
        headersToInject
      });
      if (initState.cancelled) return;
      initState.initialized = true;
      initState.enabled = !!result.enabled;
      initState.presignedUrlUser = (_a = result.presignedUrls) == null ? void 0 : _a.user;
      initState.presignedUrlAssistant = (_b = result.presignedUrls) == null ? void 0 : _b.assistant;
      if (initState.enabled && (!initState.presignedUrlUser || !initState.presignedUrlAssistant)) {
        console.warn("[voice-recorder] init returned enabled without presigned URLs; recording disabled for this call");
        initState.enabled = false;
      }
    })().catch(() => {
      initState.initialized = true;
      initState.enabled = false;
    });
    return () => {
      initState.cancelled = true;
      void teardownTrack("user", conversationId, userTrackRef);
      void teardownTrack("assistant", conversationId, assistantTrackRef);
    };
  }, [enabled, conversationId]);
  useEffect(() => {
    if (!enabled) return;
    const initState = initStateRef.current;
    if (!initState) return;
    let cancelled = false;
    const tick = async () => {
      if (initState.inFlightInit) {
        try {
          await initState.inFlightInit;
        } catch {
        }
      }
      if (cancelled) return;
      if (!initState.enabled || !initState.codec) return;
      if (userStream && !userTrackRef.current && initState.presignedUrlUser) {
        userTrackRef.current = startTrack({
          track: "user",
          stream: userStream,
          codec: initState.codec,
          presignedUrl: initState.presignedUrlUser
        });
      }
      if (assistantStream && !assistantTrackRef.current && initState.presignedUrlAssistant) {
        assistantTrackRef.current = startTrack({
          track: "assistant",
          stream: assistantStream,
          codec: initState.codec,
          presignedUrl: initState.presignedUrlAssistant
        });
      }
    };
    void tick();
    return () => {
      cancelled = true;
    };
  }, [enabled, conversationId, userStream, assistantStream]);
}
function startTrack({
  track,
  stream,
  codec,
  presignedUrl
}) {
  let recorder;
  try {
    recorder = new MediaRecorder(stream, {
      mimeType: codec.mime,
      audioBitsPerSecond: AUDIO_BITRATE
    });
  } catch (err) {
    console.warn("[voice-recorder] MediaRecorder construction failed", { track, err });
    return null;
  }
  const state = {
    track,
    recorder,
    chunks: [],
    recorderStartMs: Date.now(),
    mimeType: codec.mime,
    stopped: false,
    presignedUrl
  };
  recorder.addEventListener("dataavailable", (ev) => {
    if (ev.data && ev.data.size > 0) state.chunks.push(ev.data);
  });
  recorder.addEventListener("error", (ev) => {
    console.warn("[voice-recorder] MediaRecorder error", { track, ev });
  });
  try {
    recorder.start();
  } catch (err) {
    console.warn("[voice-recorder] MediaRecorder.start failed", { track, err });
    return null;
  }
  return state;
}
async function teardownTrack(track, conversationId, ref) {
  const state = ref.current;
  if (!state || state.stopped) return;
  state.stopped = true;
  ref.current = null;
  try {
    if (state.recorder.state !== "inactive") {
      state.recorder.stop();
    }
  } catch {
  }
  await new Promise((r) => setTimeout(r, 0));
  if (state.chunks.length === 0) {
    const headersToInject2 = await getHeadersWithUniqueVisitorId().catch(() => ({}));
    await finalizeVoiceRecording({
      conversationId,
      track,
      durationMs: 0,
      mimeType: state.mimeType,
      byteSize: 0,
      uploadOk: false,
      headersToInject: headersToInject2
    });
    return;
  }
  const blob = new Blob(state.chunks, { type: state.mimeType });
  const durationMs = Math.max(0, Date.now() - state.recorderStartMs);
  let uploadOk = false;
  try {
    const response = await fetch(state.presignedUrl, {
      method: "PUT",
      body: blob,
      headers: { "Content-Type": state.mimeType }
    });
    uploadOk = response.ok;
    if (!response.ok) {
      console.warn("[voice-recorder] PUT failed", { track, status: response.status });
    }
  } catch (err) {
    console.warn("[voice-recorder] PUT threw", { track, err });
  }
  const headersToInject = await getHeadersWithUniqueVisitorId().catch(() => ({}));
  await finalizeVoiceRecording({
    conversationId,
    track,
    durationMs,
    mimeType: state.mimeType,
    byteSize: blob.size,
    uploadOk,
    headersToInject
  });
}
export {
  useVoiceRecorder
};
//# sourceMappingURL=useVoiceRecorder.js.map
